import EmailVerificationGate from '@/components/Auth/EmailVerificationGate';
import { Stack, useNavigation, useRouter } from 'expo-router';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { StyleSheet, Platform, Pressable, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { AppSidebar } from '@/components/Sidebar/AppSidebar';
import ArtifactNavigationRail from '@/components/Sidebar/ArtifactNavigationRail';
import { useTheme } from '@/context/ThemeContext';
import { useRouteSync } from '@/hooks/useRouteSync';
import {
  useUIStore,
  useAuthStore,
  useModelStore,
  useAgentStore,
  useCollectionStore,
  useChatStore,
  useModalStore,
  useFileStore,
  useToastStore,
  useToolStore,
  usePromptStore,
  useTaskTemplatesStore,
} from '@/store';
import { setExperimentDimensions, trackUser } from '@/constants/utils/analytics';
import { isWorkComposerPath, openNewChatComposer, resetNewChatComposer } from '@/lib/openHomeComposer';
import socketManager from '@/lib/socket';
import crossTab from '@/lib/crossTab';
import useResponsiveness from '@/hooks/useResponsiveness';
import useGlobalStyles from '@/hooks/useGlobalStyles';
import WorkStarterPromptsPreloader from '@/components/Coworker/WorkStarterPromptsPreloader';
import ModalRoot from '@/components/Modal/ModalRoot';
import ToastRoot from '@/components/ToastRoot';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import LightboxRoot from '@/components/LightboxRoot';
import ArtifactViewerRoot from '@/components/Files/ArtifactViewerRoot';
import ExploreLightboxHost from '@/components/Explore/ExploreLightboxHost';
import VoiceModeOverlayHost from '@/components/VoiceMode/VoiceModeOverlayHost';
import DesktopPetHost from '@/components/Pets/DesktopPetHost';
import HomeRouteNavigationOptions from '@/components/Navigation/Routes/HomeRouteNavigationOptions';
import AgentsRouteNavigationOptions from '@/components/Navigation/Routes/AgentsRouteNavigationOptions';
import AgentSearchRouteNavigationOptions from '@/components/Navigation/Routes/AgentSearchRouteNavigationOptions';
import LibraryRouteNavigationOptions from '@/components/Navigation/Routes/LibraryRouteNavigationOptions';
import getAgentChatRouteNavigationOptions from '@/components/Navigation/Routes/AgentChatRouteNavigationOptions';
import TopicTagsRouteNavigationOptions from '@/components/Navigation/Routes/TopicTagsRouteNavigationOptions';
import { getTopicTagDetailRouteNavigationOptions } from '@/components/Navigation/Routes/TopicTagDetailRouteNavigationOptions';
import getChatRouteNavigationOptions from '@/components/Navigation/Routes/ChatRouteNavigationOptions';
import ModelsRouteNavigationOptions from '@/components/Navigation/Routes/ModelsRouteNavigationOptions';
import DeckRouteNavigationOptions from '@/components/Navigation/Routes/DeckRouteNavigationOptions';
import DeckRecipesNavigationOptions, {
  getDeckRecipesSlugRouteNavigationOptions,
  getDeckRecipesCategoryRouteNavigationOptions,
} from '@/components/Navigation/Routes/DeckRecipesNavigationOptions';
import ModelSearchRouteNavigationOptions from '@/components/Navigation/Routes/ModelSearchRouteNavigationOptions';
import getModelChatRouteNavigationOptions from '@/components/Navigation/Routes/ModelChatRouteNavigationOptions';
import FilesRouteNavigationOptions, { getFilesRouteNavigationOptions } from '@/components/Navigation/Routes/FilesRouteNavigationOptions';
import FileSearchRouteNavigationOptions from '@/components/Navigation/Routes/FileSearchRouteNavigationOptions';
import TasksRouteNavigationOptions from '@/components/Navigation/Routes/TasksRouteNavigationOptions';
import TasksBrowseRouteNavigationOptions from '@/components/Navigation/Routes/TasksBrowseRouteNavigationOptions';
import ToolsRouteNavigationOptions from '@/components/Navigation/Routes/ToolsRouteNavigationOptions';
import ToolsHistoryRouteNavigationOptions from '@/components/Navigation/Routes/ToolsHistoryRouteNavigationOptions';
import getToolDetailRouteNavigationOptions from '@/components/Navigation/Routes/ToolDetailRouteNavigationOptions';
import PromptsRouteNavigationOptions from '@/components/Navigation/Routes/PromptsRouteNavigationOptions';
import { getPromptDetailRouteNavigationOptions } from '@/components/Navigation/Routes/PromptDetailRouteNavigationOptions';
import { getTaskTemplateDetailRouteNavigationOptions } from '@/components/Navigation/Routes/TaskTemplateDetailRouteNavigationOptions';
import { getAgentCatalogDetailRouteNavigationOptions } from '@/components/Navigation/Routes/AgentCatalogDetailRouteNavigationOptions';
import { getTopicStartRouteNavigationOptions } from '@/components/Navigation/Routes/TopicStartRouteNavigationOptions';
import TopicsRouteNavigationOptions from '@/components/Navigation/Routes/TopicsRouteNavigationOptions';
import { getTopicCategoryDetailRouteNavigationOptions } from '@/components/Navigation/Routes/TopicCategoryDetailRouteNavigationOptions';
import MemoryRouteNavigationOptions from '@/components/Navigation/Routes/MemoryRouteNavigationOptions';
import ExploreRouteNavigationOptions from '@/components/Navigation/Routes/ExploreRouteNavigationOptions';
import ExploreCategoriesRouteNavigationOptions from '@/components/Navigation/Routes/ExploreCategoriesRouteNavigationOptions';
import ImagesRouteNavigationOptions from '@/components/Navigation/Routes/ImagesRouteNavigationOptions';
import VideosRouteNavigationOptions from '@/components/Navigation/Routes/VideosRouteNavigationOptions';
import MusicRouteNavigationOptions from '@/components/Navigation/Routes/MusicRouteNavigationOptions';
import BookmarksRouteNavigationOptions from '@/components/Navigation/Routes/BookmarksRouteNavigationOptions';
import ReferralRouteNavigationOptions from '@/components/Navigation/Routes/ReferralRouteNavigationOptions';
import AffiliateRouteNavigationOptions from '@/components/Navigation/Routes/AffiliateRouteNavigationOptions';
import getCollectionRouteNavigationOptions from '@/components/Navigation/Routes/CollectionRouteNavigationOptions';
import CollectionsRouteNavigationOptions from '@/components/Navigation/Routes/CollectionsRouteNavigationOptions';
import DragDropOverlay from '@/components/DragDropOverlay';
import BannerModalOverlay from '@/components/Banner/BannerModalOverlay';
import TooltipBannerLayer from '@/components/Banner/TooltipBannerLayer';
import ContextualChatDetails from '@/components/Sidebar/ContextualChatDetails';
import ContextualSidebar from '@/components/Sidebar/ContextualSidebar';
import createLazyArtifactPanel from '@/components/Files/createLazyArtifactPanel';

// Lazy, not static. On web Metro resolves the .web.jsx variants of these — 93 KB
// (spreadsheet), 84 KB (presentation), 70 KB (document) and 10 KB (PDF) of source,
// 257 KB in total. They render only when the contextual sidebar is opened to a
// matching artifact type, but a static import puts them in the entry graph
// unconditionally, so every anonymous visitor and crawler on a public catalog page
// was downloading a full spreadsheet editor behind an auth check it can never pass.
// The plain .jsx siblings are small native stubs, which is why this was easy to miss.
const PdfPreviewPanel = createLazyArtifactPanel(() => import('@/components/Files/PdfPreviewPanel'));
const PptxPreviewPanel = createLazyArtifactPanel(() => import('@/components/Files/PptxPreviewPanel'));
// Static require keeps the new panel in Metro's graph when Babel rewrites import().
const ImageEditorPanel = createLazyArtifactPanel(() => Promise.resolve({ default: require('@/components/Files/ImageEditorPanel').default }));
const CodePreviewPanel = createLazyArtifactPanel(() => import('@/components/Files/CodePreviewPanel'));
const PresentationEditorPanel = createLazyArtifactPanel(() => import('@/components/Files/PresentationEditorPanel'));
const EditableDocumentPanel = createLazyArtifactPanel(() => import('@/components/Files/EditableDocumentPanel'));
const SpreadsheetEditorPanel = createLazyArtifactPanel(() => import('@/components/Files/SpreadsheetEditorPanel'));
const VideoPreviewPanel = createLazyArtifactPanel(() => import('@/components/Files/VideoPreviewPanel'));
const VideoProjectPreviewPanel = createLazyArtifactPanel(() => import('@/components/Files/VideoProjectPreviewPanel'));

import {
  clampSidebarWidth,
  getMobileSidebarWidth,
  canFitActiveContextSidebar,
  ARTIFACT_NAV_RAIL_WIDTH,
  getNavigationSidebarWidth,
  getEffectiveContextSidebarWidth,
  isNavigationRailActive,
} from '@/constants/config';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import { usePathname } from 'expo-router';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { appendUploadedFilesToComposer } from '@/lib/chatInputContext';
import BusinessWorkspaceProvider from '@/components/Business/BusinessWorkspaceProvider';

export default function RootLayout() {
  return <EmailVerificationGate><BusinessWorkspaceProvider><AppLayout /></BusinessWorkspaceProvider></EmailVerificationGate>;
}

function AppLayout() {
  const { colors, theme } = useTheme();
  const navigation = useNavigation();
  const router = useRouter();
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { isXS, windowWidth } = useResponsiveness();
  const sidebarWidth = useUIStore(state => clampSidebarWidth(state.sidebarWidth));
  const setSidebarWidth = useUIStore(state => state.setSidebarWidth);
  const isSidebarVisible = useUIStore(state =>
    typeof state.isSidebarVisible === 'boolean' ? state.isSidebarVisible : true
  );
  const setSidebarVisible = useUIStore(state => state.setSidebarVisible);
  const toggleSidebar = useUIStore(state => state.toggleSidebar);
  const increaseBorderContrast = useUIStore(state => state.increaseBorderContrast);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarWidth = useUIStore(state => getEffectiveContextSidebarWidth(state, windowWidth));
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const contextSidebarProps = useUIStore(state => state.contextSidebarProps);
  const navigationSidebarWidth = useUIStore(state => getNavigationSidebarWidth(state, windowWidth));
  const showNavigationRail = useUIStore(isNavigationRailActive) && !isXS;
  const showNavigation = !isXS || isSidebarVisible;
  const openContextSidebar = useUIStore(state => state.openContextSidebar);
  const closeContextSidebar = useUIStore(state => state.closeContextSidebar);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const [isResizing, setIsResizing] = useState(false);
  const [localWidth, setLocalWidth] = useState(sidebarWidth);
  const userId = useAuthStore(state => state.user?.id);
  const token = useAuthStore(state => state.token);
  const { fetchAllowedFileTypes, isFileAllowed } = useFileStore(state => state.actions);
  const { openCheckout, openCreditPacksCheckout } = useGabPurchase();
  const [pwaSafeAreaTop, setPwaSafeAreaTop] = useState(0);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const getSafeAreaTop = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        window.navigator.standalone === true;
      if (!isStandalone) return 0;

      const testEl = document.createElement('div');
      testEl.style.cssText = 'position:fixed;top:0;left:0;padding-top:env(safe-area-inset-top);visibility:hidden;';
      document.body.appendChild(testEl);
      const safeArea = parseInt(getComputedStyle(testEl).paddingTop, 10) || 0;
      document.body.removeChild(testEl);

      if (safeArea > 0) return safeArea;
      return isIOS ? 47 : 20;
    };

    const updateSafeArea = () => {
      const safeArea = getSafeAreaTop();
      setPwaSafeAreaTop(safeArea);
      document.documentElement.style.setProperty('--pwa-safe-area-top', `${safeArea}px`);
    };

    updateSafeArea();
    window.addEventListener('resize', updateSafeArea);
    return () => {
      window.removeEventListener('resize', updateSafeArea);
    };
  }, []);
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);

  const resizeTimeoutRef = useRef(null);
  
  // Cross-platform pathname (native-safe)
  const pathname = usePathname?.() || (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  const previousPathnameRef = useRef(pathname);
  // Start unset so the first paint of a /c/:id (or a new browser tab)
  // closes a rehydrated document editor from another thread. Initializing
  // to `pathname` made that first paint look like "no navigation."
  const previousContextPathnameRef = useRef(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Allow public access to chat page
  const isChatableRoute = pathname.startsWith('/app') || pathname.startsWith('/c/') || pathname.startsWith('/g/') || pathname.startsWith('/m/');
  const useMobileOverlayHeader = isXS && (pathname.startsWith('/c/') || pathname.startsWith('/g/') || pathname.startsWith('/m/'));
  const isDragAndDroppableRoute = isChatableRoute || pathname.startsWith('/files') || /^\/collections\/[^/]+/.test(pathname);
  const headerBackground = useCallback(
    () => <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }} />,
    [colors.solid_color_primary],
  );
  
  // Keep route store in sync with current route
  useRouteSync();

  // Whether the user has a valid token right now (regardless of hydration state).
  // Used for socket connection — the socket just needs a token, no need to wait
  // for hydration.  If token starts as null (before persist loads) this is false,
  // and once persist restores the token from storage this becomes true.
  const hasValidToken = !!token && !isTokenExpired();

  // Sidebar/header visibility should follow token validity directly.
  // Tying this to _hasHydrated can leave layout UI hidden even when the app
  // screen itself is loaded and token is valid.
  const isAuthenticated = hasValidToken;

  useEffect(() => {
    const rawWidth = useUIStore.getState().sidebarWidth;
    const currentWidth = clampSidebarWidth(rawWidth);
    // Repair invalid preferences. Mobile drawers use their viewport
    // locally, without replacing the user's desktop menu width in storage.
    if (rawWidth !== currentWidth) {
      setSidebarWidth(currentWidth);
    }
    setLocalWidth(isXS ? getMobileSidebarWidth(windowWidth) : clampSidebarWidth(currentWidth, windowWidth));
  }, [isXS, windowWidth, sidebarWidth, setSidebarWidth]);

  const handleResize = useCallback((width, resizing) => {
    setIsResizing(resizing);
    // Commit the release position too, even when its animation frame has not run.
    const nextWidth = clampSidebarWidth(width, windowWidth);
    setLocalWidth(nextWidth);
    setSidebarWidth(nextWidth);
  }, [setSidebarWidth, windowWidth]);

  // Close sidebar only when pathname actually changes on mobile.
  // Listening to generic navigation "state" events can fire too often and
  // immediately undo manual sidebar toggles.
  useEffect(() => {
    if (!isXS) {
      previousPathnameRef.current = pathname;
      return;
    }

    const didPathChange = previousPathnameRef.current !== pathname;
    if (didPathChange && isSidebarVisible) {
      setSidebarVisible(false);
    }

    previousPathnameRef.current = pathname;
  }, [isXS, pathname, isSidebarVisible, setSidebarVisible]);

  // Close contextual sidebar only when pathname actually changes.
  useEffect(() => {
    const didPathChange = previousContextPathnameRef.current !== pathname;
    if (didPathChange && isContextSidebarVisible) {
      closeContextSidebar();
    }
    previousContextPathnameRef.current = pathname;
  }, [pathname, isContextSidebarVisible, closeContextSidebar]);

  // A navigation toggle resizes the workspace. Close an artifact only when
  // the browser itself is too narrow to hold the compact desktop split.
  useEffect(() => {
    if (!isContextSidebarVisible) return;
    if (!canFitActiveContextSidebar(useUIStore.getState(), windowWidth)) {
      closeContextSidebar();
    }
  }, [
    closeContextSidebar,
    contextSidebarType,
    isContextSidebarVisible,
    isXS,
    navigationSidebarWidth,
    windowWidth,
  ]);

  // Fetch current user data on mount
  useEffect(() => {
    if (isAuthenticated) {
      // Fetch user data
      fetchUser().catch(error => {
        console.error('Failed to fetch user data:', error);
      });

      // Fetch allowed file types
      fetchAllowedFileTypes().catch(error => {
        console.error('Failed to fetch allowed file types:', error);
      });
    }
  }, [isAuthenticated, fetchUser]);

  useEffect(() => {
    if (userId) {
      trackUser(userId.toString());
    }
  }, [userId]);

  // Auto-trigger checkout from deep link (URL params first, localStorage fallback for post-login)
  const pendingCheckoutHandled = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !userId || pendingCheckoutHandled.current) return;
    try {
      let pending = null;

      // Primary: read directly from current URL (avoids parent useEffect race)
      const params = new URLSearchParams(window.location.search);
      const checkoutType = params.get('checkout');
      if (checkoutType) {
        pending = { type: checkoutType, ts: Date.now() };
        const plan = params.get('plan');
        const promo = params.get('promo');
        const pack = params.get('pack');
        if (plan) pending.plan = plan;
        if (promo) pending.promo = promo;
        if (pack) pending.pack = pack;

        // Strip params from URL
        params.delete('checkout');
        params.delete('plan');
        params.delete('promo');
        params.delete('pack');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
        localStorage.removeItem('pendingCheckout');
      }

      // Fallback: check localStorage (set by root _layout.jsx for pre-auth deep links)
      if (!pending) {
        const raw = localStorage.getItem('pendingCheckout');
        if (!raw) return;
        localStorage.removeItem('pendingCheckout');
        pending = JSON.parse(raw);
        const MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours
        if (pending.ts && Date.now() - pending.ts > MAX_AGE_MS) return;
      }

      pendingCheckoutHandled.current = true;

      setTimeout(() => {
        if (pending.type === 'plus') {
          openCheckout({
            plusPlanId: pending?.plan || 'yearly',
            promoCode: pending?.promo || undefined,
          }).catch(err => console.warn('[DeepLink] checkout error:', err));
        } else if (pending.type === 'credits' && pending.pack) {
          openCreditPacksCheckout({
            creditPackId: pending?.pack || undefined,
            promoCode: pending?.promo || undefined,
          }).catch(err => console.warn('[DeepLink] credit checkout error:', err));
        } else if (pending.type === 'credits') {
          openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
        }
      }, 500);
    } catch (e) {
      console.warn('[DeepLink] Failed to process pending checkout:', e);
    }
  }, [isAuthenticated, userId, openCheckout, openCreditPacksCheckout]);

  // Initialize and test socket connection on mount.
  // Uses hasValidToken (not isAuthenticated) so the socket connects as soon as
  // the token is available from Zustand persist, without waiting for _hasHydrated.
  useEffect(() => {
    // If we have a valid token but socket isn't connected, connect it
    if (hasValidToken && !socketManager.isConnected) {
      console.log('Initializing socket connection...');
      socketManager.connect(token, userId);
    } else if (!hasValidToken) {
      socketManager.disconnectSocket();
    }

    let retryTimer = null;
    const testConnection = () => {
      if (socketManager.isConnected) {
        socketManager.emitPing(userId);
      } else {
        retryTimer = setTimeout(testConnection, 2000);
      }
    };

    // Initial attempt after a short delay
    const timer = setTimeout(testConnection, 1000);

    return () => {
      clearTimeout(timer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [hasValidToken, token, userId]);

  // Update sidebar visibility when screen size changes
  useEffect(() => {
    const shouldShowSidebar = Platform.OS === 'web' && !isXS;
    setSidebarVisible(shouldShowSidebar);
  }, [isXS, setSidebarVisible]);

  // Service-Worker update check — leader-gated and visibility-gated.
  //
  // Previously this ran in the inline SW bootstrap script (`app/+html.jsx`)
  // as a `setInterval` in EVERY open tab, generating N hourly GET /sw.js
  // requests for N tabs. Two improvements:
  //   1. Only the cross-tab leader (`lib/crossTab.js`) runs the interval, so
  //      10 idle tabs produce 1 hourly check instead of 10.
  //   2. The check itself bails out while `document.hidden`, and a
  //      `visibilitychange` listener triggers an immediate check on
  //      becoming visible — so the request only fires when a user is
  //      actually around to benefit from the update.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    if (typeof document === 'undefined') return;

    const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
    let intervalId = null;
    let visibilityHandler = null;
    let lastCheckAt = 0;

    const checkOnce = async () => {
      if (document.hidden) return;
      if (Date.now() - lastCheckAt < CHECK_INTERVAL_MS) return;
      lastCheckAt = Date.now();
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      } catch (_) {
        // transient — browser will retry next interval
      }
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(checkOnce, CHECK_INTERVAL_MS);
      visibilityHandler = () => {
        if (!document.hidden) checkOnce();
      };
      document.addEventListener('visibilitychange', visibilityHandler);
      checkOnce();
    };

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
      }
    };

    const leaderUnsub = crossTab.onLeaderChange((isLeader) => {
      if (isLeader) start();
      else stop();
    });

    return () => {
      stop();
      if (leaderUnsub) leaderUnsub();
    };
  }, []);

  // Keyboard shortcuts that open per-user modals (`SettingsModalWeb`,
  // `FileUploadModal`, temp-chat URL) need the AuthCTA when the user is
  // logged out — otherwise the shortcut silently opens a modal whose
  // underlying API calls 401. We use the cheap `hasValidToken` boolean
  // captured in closure so the handlers stay sync.
  const keyboardShortcutHandlers = useMemo(() => {
    const gate = (action, ctx) => () => {
      if (isAuthenticated) {
        action();
        return;
      }
      openModal('AuthCTAModal', { ...ctx, source: ctx?.source || 'keyboard_shortcut' });
    };
    return {
      TOGGLE_SIDEBAR: toggleSidebar,
      NEW_CHAT: () => openNewChatComposer(router),
      NEW_WORK: () => openNewChatComposer(router, { work: true }),
      NEW_TEMP_CHAT: gate(
        () => {
          resetNewChatComposer();
          router.replace('/app?temporary-chat=true');
        },
        {
          source: 'keyboard_shortcut_temp_chat',
          title: 'Sign in to use Temporary chat',
          subtitle: 'Temporary chats are tied to your account so we can clean them up automatically.',
          icon: 'eye-slash',
        },
      ),
      OPEN_SHORTCUTS: gate(
        () => openModal('SettingsModalWeb', { tab: 'keyboard' }),
        {
          source: 'keyboard_shortcut_shortcuts',
          title: 'Sign in to open Settings',
          subtitle: 'Keyboard shortcuts live in Settings, which is tied to your account.',
          icon: 'gear',
        },
      ),
      OPEN_FILE_UPLOAD: gate(
        () => openModal('FileUploadModal', { libraryUpload: pathnameRef.current.startsWith('/files') }),
        {
          source: 'keyboard_shortcut_file_upload',
          title: 'Sign in to upload files',
          subtitle: 'File uploads are saved to your account so chats can reference them later.',
          icon: 'cloud-arrow-up',
        },
      ),
      GO_TO_AGENTS: () => router.push('/agents'),
      GO_TO_HOME: () => router.push('/app'),
      GO_TO_FILES: () => router.push('/files'),
      GO_TO_MODELS: () => router.push('/models'),
      GO_TO_SETTINGS: gate(
        () => openModal('SettingsModalWeb'),
        {
          source: 'keyboard_shortcut_settings',
          title: 'Sign in to open Settings',
          subtitle: 'Settings are tied to your account — create one or log in to edit your preferences.',
          icon: 'gear',
        },
      ),
    };
  }, [toggleSidebar, router, openModal, isAuthenticated]);

  useKeyboardShortcuts(keyboardShortcutHandlers);

  useGlobalStyles();

  // Add drag and drop handlers - only for chat routes
  useEffect(() => {
    if (Platform.OS !== 'web' || !isDragAndDroppableRoute) return;

    // Only OS-level file drags should raise the upload overlay. Drags that
    // originate inside the page (e.g. moving an image around the document
    // editor) carry text/html instead of Files — those must fall through
    // untouched, because calling preventDefault on them would also cancel
    // the browser's native contentEditable move.
    const isFileDrag = (e) => {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      return Array.from(types).includes('Files');
    };

    const handleDragEnter = (e) => {
      // An open upload queue owns its own drop target. Do not cover it with
      // the global overlay or replace files already selected in the dialog.
      if (useModalStore.getState().isOpen) return;
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      
      // Clear any existing timeout
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
      
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      
      // Only set timeout if actually leaving the window
      if (e.relatedTarget === null) {
        const timeout = setTimeout(() => {
          setIsDragging(false);
          setDragTimeout(null);
        }, 100);
        setDragTimeout(timeout);
      }
    };

    const handleDrop = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
      setIsDragging(false);
    };

    const handleDragOver = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('dragover', handleDragOver);

    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('dragover', handleDragOver);
    };
  }, [isDragAndDroppableRoute, dragTimeout]);

  const handleFileDrop = useCallback((files) => {
    setIsDragging(false);
    const { addToast } = useToastStore.getState().actions;
    
    if (files.length === 0) {
      console.log('No files provided, just closing overlay');
      return;
    }

    // Filter out files that aren't allowed
    const allowedFiles = [];
    const rejectedFiles = [];

    files.forEach(file => {
      const { isAllowed, message } = isFileAllowed(file);
      if (isAllowed) {
        allowedFiles.push(file);
      } else {
        rejectedFiles.push({ file, message });
      }
    });

    // Show error toast if any files were rejected
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({file, message }) => {
        if (file && message) {
          addToast(message, 'error');
        }
      });
    
      // If all files were rejected, don't open modal
      if (allowedFiles.length === 0) return;
    }
    
    openModal('FileUploadModal', {
      files: allowedFiles,
      libraryUpload: pathnameRef.current.startsWith('/files'),
      shouldAutoUpload: true,
      onUpload: (uploadedFiles) => {
        // Chat: a single office file opens in its editor so the user can
        // revise it in place. Work: keep the file on the composer — the
        // agent needs the attachment to analyze, not an editor sidebar.
        const pathname = pathnameRef.current || '';
        if (pathname.startsWith('/files')) return;
        const successes = uploadedFiles?.results?.filter(
          (result) => result.status === 'success' && result.uploadedFile,
        ) || [];
        if (successes.length === 1 && !isWorkComposerPath(pathname)) {
          const { maybeOpenUploadedFileInEditor } = require('@/constants/utils/files');
          if (maybeOpenUploadedFileInEditor(successes[0].uploadedFile)) return;
        }
        appendUploadedFilesToComposer(pathname, uploadedFiles);
      },
      onClose: () => {
        closeModal();
        setIsDragging(false);
      }
    });
  }, []); // Remove openModal from dependencies since we're getting it fresh from the store

  // Dashboard is publicly accessible. Personal/destructive actions inside
  // individual screens, the sidebar, and the composer are responsible for
  // gating themselves behind the auth CTA when `!isAuthenticated` — see
  // docs/PUBLIC_DASHBOARD_TODO.md. Auth-only side effects (socket connect,
  // `fetchUser`, deep-link checkout handler, drag-and-drop upload overlay,
  // etc.) already short-circuit on `!isAuthenticated` above.

  const useWidth = isXS ? localWidth : clampSidebarWidth(localWidth, windowWidth);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ImpersonationBanner />
      <View style={[styles.container, { backgroundColor: colors.solid_color_block }]}>
        {/* Add DragDropOverlay only for chat routes */}
        {(isDragAndDroppableRoute && isAuthenticated) && (
          <DragDropOverlay
            colors={colors}
            visible={isDragging}
            description={pathname.startsWith('/files') ? 'Release to add files to your library' : 'Release to upload files to the chat'}
            onDrop={handleFileDrop}
          />
        )}

        <View aria-hidden={!showNavigation} style={[
          styles.sidebarContainer,
          {
            width: showNavigationRail ? ARTIFACT_NAV_RAIL_WIDTH : useWidth,
            transform: [{ 
              translateX: showNavigation ? 0 : -useWidth
            }],
            pointerEvents: showNavigation ? 'auto' : 'none',
            // Offscreen transforms alone leave every chat reachable to
            // screen readers and keyboard users while the mobile drawer is shut.
            visibility: showNavigation ? 'visible' : 'hidden',
          },
          !isResizing && styles.animated,
        ]}>
          {showNavigationRail ? (
            <ArtifactNavigationRail pwaSafeAreaTop={pwaSafeAreaTop} />
          ) : (
            <AppSidebar
              width={useWidth}
              onResize={handleResize}
              pwaSafeAreaTop={pwaSafeAreaTop}
            />
          )}
        </View>

        <View style={[
          styles.mainContent,
          {
            marginLeft: isXS ? 0 : navigationSidebarWidth,
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative',
            transform: [{ translateX: Platform.OS !== 'web' && isSidebarVisible ? useWidth : 0 }],
          },
          !isResizing && styles.mainContentAnimated
        ]}>
          <View style={[styles.mainContentInner, {
            backgroundColor: colors.solid_color_primary,
            minWidth: 0,
            marginRight: (!isXS && isContextSidebarVisible) ? (contextSidebarWidth || 0) : 0,
          }]}>
            {isXS && (
              <Pressable 
                style={[
                  styles.overlay,
                  styles.animated,
                  {
                    opacity: isSidebarVisible ? 1 : 0,
                    pointerEvents: isSidebarVisible ? 'auto' : 'none',
                  }
                ]} 
                onPress={toggleSidebar}
              />
            )}

            <View style={[styles.stackContainer, { backgroundColor: colors.solid_color_primary, minWidth: 0 }]}>
              <Stack 
                freezeOnBlur={true}
                detachPreviousScreen={true}
                screenOptions={{ 
                  freezeOnBlur: true,
                  detachPreviousScreen: true,
                  // Frozen /c/:id screens leave 0×0 composers in the DOM.
                  // New Chat then types into the ghost Work box; Send never
                  // appears on the live home composer. Native already unmounts.
                  unmountOnBlur: true,
                  headerShown: true,
                  headerTransparent: useMobileOverlayHeader,
                  headerBackground: useMobileOverlayHeader ? () => null : headerBackground,
                  headerShadowVisible: false,
                  headerTitle: () => null,
                  headerTitleAlign: 'center',
                  headerTintColor: colors.text_color_primary,
                  contentStyle: {
                    backgroundColor: colors.solid_color_primary,
                    paddingTop: 0,
                    marginTop: useMobileOverlayHeader ? -(53 + pwaSafeAreaTop + 8) : 0,
                  },
                  headerStyle: Platform.select({
                    web: {
                      height: 53 + pwaSafeAreaTop + 8,
                      backgroundColor: useMobileOverlayHeader ? 'transparent' : colors.solid_color_primary,
                      position: useMobileOverlayHeader ? 'absolute' : undefined,
                      top: useMobileOverlayHeader ? 0 : undefined,
                      left: useMobileOverlayHeader ? 0 : undefined,
                      right: useMobileOverlayHeader ? 0 : undefined,
                      zIndex: useMobileOverlayHeader ? 10 : undefined,
                      borderBottomWidth: 0,
                      borderBottomColor: colors.solid_color_primary,
                      boxShadow: 'none',
                    },
                    default: {
                      backgroundColor: colors.solid_color_primary,
                      borderBottomWidth: increaseBorderContrast ? 1 : 0,
                      borderBottomColor: increaseBorderContrast ? colors.border_color_secondary : 'transparent',
                    },
                  }),
                  headerTitleStyle: {
                    color: colors.text_color_primary,
                    fontWeight: 400,
                  },
                  headerLeft: (props) => {
                    return (
                      <DefaultRouteNavigationHeaderLeft />
                    )
                  },
                }}
              >
                <Stack.Screen
                  name="app"
                  options={HomeRouteNavigationOptions}
                />
                
                <Stack.Screen 
                  name="agents/create"
                  options={{ headerShown: false, title: "Create a bot" }}
                />
                <Stack.Screen
                  name="agents/index"
                  options={{ ...AgentsRouteNavigationOptions, headerShown: isXS }}
                />
                <Stack.Screen 
                  name="agents/search"
                  options={AgentSearchRouteNavigationOptions}
                />
                <Stack.Screen
                  name="agents/category/[slug]"
                  options={{ ...AgentsRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen
                  name="agents/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const agent = slug
                      ? Object.values(useAgentStore.getState().agents || {}).find(
                        (a) => a && String(a.slug || '').toLowerCase() === String(slug).toLowerCase(),
                      )
                      : null;
                    const title = agent?.name || (slug
                      ? String(slug).replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
                      : 'Agent');
                    return getAgentCatalogDetailRouteNavigationOptions(title);
                  }}
                />

                <Stack.Screen
                  name="start/[slug]"
                  options={getTopicStartRouteNavigationOptions}
                />

                <Stack.Screen name="topics/index" options={TopicsRouteNavigationOptions} />
                <Stack.Screen
                  name="topics/category/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const title = slug
                      ? String(slug).replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
                      : 'Topics';
                    return getTopicCategoryDetailRouteNavigationOptions(title);
                  }}
                />
                <Stack.Screen 
                  name="library/index"
                  options={LibraryRouteNavigationOptions}
                />

                <Stack.Screen name="tags/index" options={TopicTagsRouteNavigationOptions} />

                <Stack.Screen
                  name="tags/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const title = slug
                      ? String(slug).replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
                      : 'Tag';
                    return getTopicTagDetailRouteNavigationOptions(title);
                  }}
                />

                <Stack.Screen 
                  name="c/[id]" 
                  options={({ route }) => {
                    return getChatRouteNavigationOptions(route.params?.id, isXS);
                  }}
                />
                
                <Stack.Screen 
                  name="g/[id]"
                  options={({ route }) => {
                    const agent = useAgentStore.getState().getAgentById(route.params?.id);
                    return getAgentChatRouteNavigationOptions(agent, isXS);
                  }}
                />

                <Stack.Screen 
                  name="m/[id]"
                  options={({ route }) => {
                    const model = useModelStore.getState().getModelById(route.params?.id);
                    return {
                      ...getModelChatRouteNavigationOptions(model, isXS),
                      // Model introductions belong below the navigation bar,
                      // unlike conversation timelines that use the overlay header.
                      headerTransparent: false,
                      headerBackground,
                      headerStyle: {
                        height: 53 + pwaSafeAreaTop + 8,
                        backgroundColor: colors.solid_color_primary,
                      },
                      contentStyle: {
                        backgroundColor: colors.solid_color_primary,
                        paddingTop: 0,
                        marginTop: 0,
                      },
                    };
                  }}
                />

                <Stack.Screen 
                  name="models/index"
                  options={{ ...ModelsRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen 
                  name="models/search"
                  options={ModelSearchRouteNavigationOptions}
                />

                <Stack.Screen 
                  name="files/index"
                  options={getFilesRouteNavigationOptions(isXS)}
                />

                <Stack.Screen 
                  name="files/search"
                  options={FileSearchRouteNavigationOptions}
                />

                <Stack.Screen
                  name="collections/index"
                  options={CollectionsRouteNavigationOptions}
                />
                <Stack.Screen 
                  name="collections/[id]"
                  options={({ route }) => {
                    const collectionId = route.params?.id;
                    const collection = useCollectionStore.getState().collections[collectionId];
                    return getCollectionRouteNavigationOptions(collection, collectionId);
                  }}
                />

                <Stack.Screen 
                  name="tasks/index"
                  options={{ ...TasksRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen
                  name="tasks/browse"
                  options={{ ...TasksBrowseRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen
                  name="coworker/index"
                  options={{ headerShown: false, title: 'Work' }}
                />

                <Stack.Screen
                  name="coworker/[threadId]/index"
                  options={{ title: 'Thread', headerBackVisible: true }}
                />

                <Stack.Screen
                  name="coworker/schedules"
                  options={{ title: 'Scheduled work', headerBackVisible: true }}
                />

                <Stack.Screen
                  name="tasks/template/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const tpl = slug
                      ? useTaskTemplatesStore.getState().templates.find((t) => t.slug === slug)
                      : null;
                    const title = tpl?.name || (slug
                      ? String(slug).replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
                      : 'Task template');
                    return getTaskTemplateDetailRouteNavigationOptions(title, slug);
                  }}
                />

                <Stack.Screen
                  name="tools/index"
                  options={{ ...ToolsRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen
                  name="tools/history"
                  options={{ ...ToolsHistoryRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen
                  name="tools/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const tool = slug ? useToolStore.getState().toolsBySlug[slug] : null;
                    return getToolDetailRouteNavigationOptions(tool, slug);
                  }}
                />

                <Stack.Screen
                  name="prompts/index"
                  options={PromptsRouteNavigationOptions}
                />

                <Stack.Screen
                  name="prompts/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const prompt = slug ? usePromptStore.getState().promptsBySlug[slug] : null;
                    return getPromptDetailRouteNavigationOptions(prompt, slug);
                  }}
                />

                <Stack.Screen
                  name="prompts/category/[slug]"
                  options={PromptsRouteNavigationOptions}
                />

                {/* <Stack.Screen
                  name="workflows/index"
                  options={{
                    title: 'Workflows',
                    headerTitle: 'Workflows',
                    headerBackVisible: false,
                  }}
                />
                <Stack.Screen
                  name="workflows/[slug]/index"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const wf = slug ? useWorkflowStore.getState().workflowsBySlug[slug] : null;
                    const title = wf?.name || 'Workflow';
                    return { title, headerTitle: title, headerBackVisible: true };
                  }}
                />
                <Stack.Screen
                  name="workflows/[slug]/edit"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const wf = slug ? useWorkflowStore.getState().workflowsBySlug[slug] : null;
                    const title = wf?.name ? `Edit · ${wf.name}` : 'Edit workflow';
                    return { title, headerTitle: title, headerShown: false };
                  }}
                /> */}

                <Stack.Screen name="maps" options={{ title: 'Maps', headerTitle: () => null, headerBackVisible: false, headerShown: isXS }} />
                <Stack.Screen name="ideas" options={{ title: 'Ideas', headerTitle: () => null, headerBackVisible: false, headerShown: isXS }} />

                <Stack.Screen 
                  name="memory"
                  options={{ ...MemoryRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen
                  name="explore/index"
                  options={ExploreRouteNavigationOptions}
                />

                <Stack.Screen
                  name="explore/item/[slug]"
                  options={{ ...ExploreRouteNavigationOptions, headerBackVisible: true }}
                />

                <Stack.Screen
                  name="explore/categories"
                  options={ExploreCategoriesRouteNavigationOptions}
                />

                <Stack.Screen
                  name="explore/category/[slug]"
                  options={ExploreRouteNavigationOptions}
                />

                <Stack.Screen 
                  name="images/index"
                  options={ImagesRouteNavigationOptions}
                />

                <Stack.Screen 
                  name="videos/index"
                  options={VideosRouteNavigationOptions}
                />

                <Stack.Screen 
                  name="music/index"
                  options={MusicRouteNavigationOptions}
                />

                <Stack.Screen 
                  name="bookmarks"
                  options={{ ...BookmarksRouteNavigationOptions, headerShown: isXS }}
                />

                <Stack.Screen 
                  name="referral"
                  options={ReferralRouteNavigationOptions}
                />

                <Stack.Screen
                  name="affiliate"
                  options={AffiliateRouteNavigationOptions}
                />

                <Stack.Screen
                  name="settings"
                  options={{ headerShown: false }}
                />

                <Stack.Screen
                  name="deck"
                  options={DeckRouteNavigationOptions}
                />

                <Stack.Screen
                  name="decks/recipes/index"
                  options={DeckRecipesNavigationOptions}
                />

                <Stack.Screen
                  name="decks/recipes/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const label = slug
                      ? String(slug).replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
                      : null;
                    return getDeckRecipesSlugRouteNavigationOptions(label);
                  }}
                />

                <Stack.Screen
                  name="decks/recipes/category/[slug]"
                  options={({ route }) => {
                    const slug = route.params?.slug;
                    const label = slug
                      ? String(slug).replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
                      : null;
                    return getDeckRecipesCategoryRouteNavigationOptions(label);
                  }}
                />


              </Stack>
            </View>
          </View>

          {isAuthenticated && (
            <ContextualSidebar
              isVisible={isContextSidebarVisible}
              onClose={closeContextSidebar}
              pwaSafeAreaTop={pwaSafeAreaTop}
            >
              {contextSidebarType === 'chat-details' && (
                <ContextualChatDetails
                  isModal={false}
                  onClose={closeContextSidebar}
                  {...(contextSidebarProps || {})}
                />
              )}
              <>
                {contextSidebarType === 'pdf-preview' && (
                  <PdfPreviewPanel
                    file={contextSidebarProps?.file}
                    onClose={closeContextSidebar}
                  />
                )}
                {contextSidebarType === 'pptx-preview' && (
                  <PptxPreviewPanel
                    file={contextSidebarProps?.file}
                    onClose={closeContextSidebar}
                  />
                )}
                {contextSidebarType === 'image-editor' && (
                  <ImageEditorPanel file={contextSidebarProps?.file} onClose={closeContextSidebar} />
                )}
                {contextSidebarType === 'code-preview' && (
                  <CodePreviewPanel
                    file={contextSidebarProps?.file}
                    onClose={closeContextSidebar}
                  />
                )}
                {contextSidebarType === 'presentation-editor' && (
                  <PresentationEditorPanel
                    file={contextSidebarProps?.file}
                    onClose={closeContextSidebar}
                  />
                )}
                {contextSidebarType === 'spreadsheet-editor' && (
                  <SpreadsheetEditorPanel
                    file={contextSidebarProps?.file}
                    onClose={closeContextSidebar}
                  />
                )}
                {contextSidebarType === 'document-editor' && (
                  <EditableDocumentPanel
                    file={contextSidebarProps?.file}
                    onClose={closeContextSidebar}
                  />
                )}
                {contextSidebarType === 'video-preview' && (
                  <VideoPreviewPanel file={contextSidebarProps?.file} onClose={closeContextSidebar} />
                )}
                {contextSidebarType === 'video-project-preview' && (
                  <VideoProjectPreviewPanel
                    file={contextSidebarProps?.file}
                    artifacts={contextSidebarProps?.artifacts}
                    files={contextSidebarProps?.files}
                    scriptText={contextSidebarProps?.scriptText}
                    taskId={contextSidebarProps?.taskId}
                    conversationId={contextSidebarProps?.conversationId}
                    selectedId={contextSidebarProps?.selectedId}
                    onClose={closeContextSidebar}
                  />
                )}
              </>
            </ContextualSidebar>
          )}
        </View>
      </View>
      <BannerModalOverlay />
      <TooltipBannerLayer />
      <ExploreLightboxHost />
      <VoiceModeOverlayHost />
      <DesktopPetHost />
      <LightboxRoot />
      <ArtifactViewerRoot />
      <ModalRoot />
      <ToastRoot />
      <WorkStarterPromptsPreloader />
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  sidebarContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 100,
    elevation: 100,
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  mainContentInner: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
  },
  animated: {
    transition: 'all 0.3s ease-in-out',
  },
  mainContentAnimated: {
    transition: 'all 0.3s ease-in-out',
  },
  stackContainer: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 99,
  },
});
